import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Heart, Shield, Cookie, Mail, Globe, FileText, BookOpen, Scale, Calculator, Newspaper, Wrench } from 'lucide-react';

// SEO Links Configuration - Multi-language support
const SEO_LINKS = {
  popularCalculators: [
    { slug: 'germany', name: { en: 'Germany VAT', de: 'Deutschland MwSt', fr: 'TVA Allemagne', it: 'IVA Germania', es: 'IVA Alemania' } },
    { slug: 'france', name: { en: 'France VAT', de: 'Frankreich MwSt', fr: 'TVA France', it: 'IVA Francia', es: 'IVA Francia' } },
    { slug: 'italy', name: { en: 'Italy VAT', de: 'Italien MwSt', fr: 'TVA Italie', it: 'IVA Italia', es: 'IVA Italia' } },
    { slug: 'spain', name: { en: 'Spain VAT', de: 'Spanien MwSt', fr: 'TVA Espagne', it: 'IVA Spagna', es: 'IVA España' } },
    { slug: 'united-kingdom', name: { en: 'UK VAT', de: 'UK MwSt', fr: 'TVA UK', it: 'IVA UK', es: 'IVA UK' } },
    { slug: 'netherlands', name: { en: 'Netherlands VAT', de: 'Niederlande MwSt', fr: 'TVA Pays-Bas', it: 'IVA Paesi Bassi', es: 'IVA Países Bajos' } },
    { slug: 'belgium', name: { en: 'Belgium VAT', de: 'Belgien MwSt', fr: 'TVA Belgique', it: 'IVA Belgio', es: 'IVA Bélgica' } },
    { slug: 'austria', name: { en: 'Austria VAT', de: 'Österreich MwSt', fr: 'TVA Autriche', it: 'IVA Austria', es: 'IVA Austria' } },
    { slug: 'australia', name: { en: 'Australia GST', de: 'Australien GST', fr: 'TPS Australie', it: 'GST Australia', es: 'GST Australia' } },
    { slug: 'japan', name: { en: 'Japan VAT', de: 'Japan MwSt', fr: 'TVA Japon', it: 'IVA Giappone', es: 'IVA Japón' } },
    { slug: 'brazil', name: { en: 'Brazil VAT', de: 'Brasilien MwSt', fr: 'TVA Brésil', it: 'IVA Brasile', es: 'IVA Brasil' } },
    { slug: 'south-africa', name: { en: 'South Africa VAT', de: 'Südafrika MwSt', fr: 'TVA Afrique du Sud', it: 'IVA Sudafrica', es: 'IVA Sudáfrica' } },
    { slug: 'hungary', name: { en: 'Hungary VAT', de: 'Ungarn MwSt', fr: 'TVA Hongrie', it: 'IVA Ungheria', es: 'IVA Hungría' } },
    { slug: 'greece', name: { en: 'Greece VAT', de: 'Griechenland MwSt', fr: 'TVA Grèce', it: 'IVA Grecia', es: 'IVA Grecia' } },
  ],
  popularArticles: [
    { slug: 'vat-rates-europe-2025', name: { en: 'VAT Rates Europe 2025', de: 'MwSt Sätze Europa 2025', fr: 'Taux TVA Europe 2025', it: 'Aliquote IVA Europa 2025', es: 'Tipos IVA Europa 2025' } },
    { slug: 'how-to-calculate-vat', name: { en: 'How to Calculate VAT', de: 'MwSt Berechnen', fr: 'Calculer la TVA', it: 'Come Calcolare IVA', es: 'Cómo Calcular IVA' } },
    { slug: 'vat-refund-tourists', name: { en: 'VAT Refund for Tourists', de: 'MwSt Rückerstattung', fr: 'Remboursement TVA', it: 'Rimborso IVA Turisti', es: 'Devolución IVA' } },
  ],
  tools: [
    { path: '/bulk-calculator', name: { en: 'Bulk Calculator', de: 'Massenrechner', fr: 'Calcul en Masse', it: 'Calcolo Multiplo', es: 'Cálculo Masivo' } },
    { path: '/invoice-calculator', name: { en: 'Invoice Calculator', de: 'Rechnungsrechner', fr: 'Calcul Facture', it: 'Calcolo Fattura', es: 'Cálculo Factura' } },
  ]
};

const FOOTER_TRANSLATIONS = {
  en: {
    brand: 'vat.expert',
    features: 'Features',
    legal: 'Legal',
    resources: 'Resources',
    blog: 'VAT Blog',
    blogDesc: 'Guides, news and tips',
    accessibility: 'Accessibility',
    popularCalculators: 'Popular Calculators',
    popularArticles: 'Popular Articles',
    tools: 'Tools',
    allCalculators: 'All Calculators',
    allArticles: 'All Articles',
    countries: '62 countries supported',
    languages: '5 languages (EN, DE, FR, IT, ES)',
    bulk: 'Bulk CSV processing (up to 1000 items)',
    invoice: 'Invoice calculator with multi-rate',
    history: 'Calculation history export',
    wcag: 'WCAG 2.1 AA/AAA accessible',
    keyboard: 'Keyboard navigation',
    screenReader: 'Screen reader compatible',
    contrast: 'High contrast mode',
    fontSize: 'Adjustable font size',
    motion: 'Reduced motion option',
    dyslexia: 'Dyslexia-friendly font',
    privacy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy',
    cookieSettings: 'Cookie Settings',
    terms: 'Terms of Service',
    contact: 'Contact',
    madeWith: 'Made with',
    forBusiness: 'for businesses worldwide',
    rights: 'All rights reserved.',
    wcagCompliance: 'This website strives to be compliant with WCAG 2.1 Level AA/AAA standards.',
    accessibilityContact: 'If you experience any accessibility issues, please'
  },
  de: {
    brand: 'vat.expert',
    features: 'Funktionen',
    legal: 'Rechtliches',
    resources: 'Ressourcen',
    blog: 'MwSt-Blog',
    blogDesc: 'Anleitungen, News und Tipps',
    accessibility: 'Barrierefreiheit',
    popularCalculators: 'Beliebte Rechner',
    popularArticles: 'Beliebte Artikel',
    tools: 'Werkzeuge',
    allCalculators: 'Alle Rechner',
    allArticles: 'Alle Artikel',
    countries: '62 Länder unterstützt',
    languages: '5 Sprachen (EN, DE, FR, IT, ES)',
    bulk: 'CSV-Massenberechnung (bis zu 1000 Posten)',
    invoice: 'Rechnungsrechner mit mehreren Sätzen',
    history: 'Export der Berechnungshistorie',
    wcag: 'WCAG 2.1 AA/AAA barrierefrei',
    keyboard: 'Tastaturnavigation',
    screenReader: 'Screenreader-kompatibel',
    contrast: 'Hoher Kontrast',
    fontSize: 'Anpassbare Schriftgröße',
    motion: 'Reduzierte Bewegung',
    dyslexia: 'Legasthenie-freundliche Schrift',
    privacy: 'Datenschutzerklärung',
    cookiePolicy: 'Cookie-Richtlinie',
    cookieSettings: 'Cookie-Einstellungen',
    terms: 'Nutzungsbedingungen',
    contact: 'Kontakt',
    madeWith: 'Gemacht mit',
    forBusiness: 'für Unternehmen weltweit',
    rights: 'Alle Rechte vorbehalten.',
    wcagCompliance: 'Diese Website strebt die Einhaltung der WCAG 2.1 Level AA/AAA Standards an.',
    accessibilityContact: 'Bei Barrierefreiheitsproblemen kontaktieren Sie uns bitte'
  },
  fr: {
    brand: 'vat.expert',
    features: 'Fonctionnalités',
    legal: 'Mentions légales',
    resources: 'Ressources',
    blog: 'Blog TVA',
    blogDesc: 'Guides, actualités et conseils',
    accessibility: 'Accessibilité',
    popularCalculators: 'Calculateurs Populaires',
    popularArticles: 'Articles Populaires',
    tools: 'Outils',
    allCalculators: 'Tous les Calculateurs',
    allArticles: 'Tous les Articles',
    countries: '62 pays supportés',
    languages: '5 langues (EN, DE, FR, IT, ES)',
    bulk: 'Traitement CSV en masse (jusqu\'à 1000 éléments)',
    invoice: 'Calculateur de facture multi-taux',
    history: 'Export de l\'historique des calculs',
    wcag: 'WCAG 2.1 AA/AAA accessible',
    keyboard: 'Navigation clavier',
    screenReader: 'Compatible lecteur d\'écran',
    contrast: 'Mode contraste élevé',
    fontSize: 'Taille de police ajustable',
    motion: 'Mouvement réduit',
    dyslexia: 'Police adaptée à la dyslexie',
    privacy: 'Politique de confidentialité',
    cookiePolicy: 'Politique des Cookies',
    cookieSettings: 'Paramètres des Cookies',
    terms: 'Conditions d\'utilisation',
    contact: 'Contact',
    madeWith: 'Fait avec',
    forBusiness: 'pour les entreprises du monde entier',
    rights: 'Tous droits réservés.',
    wcagCompliance: 'Ce site s\'efforce d\'être conforme aux normes WCAG 2.1 niveau AA/AAA.',
    accessibilityContact: 'Si vous rencontrez des problèmes d\'accessibilité, veuillez'
  },
  it: {
    brand: 'vat.expert',
    features: 'Funzionalità',
    legal: 'Legale',
    resources: 'Risorse',
    blog: 'Blog IVA',
    blogDesc: 'Guide, novità e consigli',
    accessibility: 'Accessibilità',
    popularCalculators: 'Calcolatori Popolari',
    popularArticles: 'Articoli Popolari',
    tools: 'Strumenti',
    allCalculators: 'Tutti i Calcolatori',
    allArticles: 'Tutti gli Articoli',
    countries: '62 paesi supportati',
    languages: '5 lingue (EN, DE, FR, IT, ES)',
    bulk: 'Elaborazione CSV in massa (fino a 1000 elementi)',
    invoice: 'Calcolatore fatture multi-aliquota',
    history: 'Esportazione cronologia calcoli',
    wcag: 'WCAG 2.1 AA/AAA accessibile',
    keyboard: 'Navigazione da tastiera',
    screenReader: 'Compatibile con screen reader',
    contrast: 'Modalità alto contrasto',
    fontSize: 'Dimensione carattere regolabile',
    motion: 'Movimento ridotto',
    dyslexia: 'Font per dislessia',
    privacy: 'Informativa Privacy',
    cookiePolicy: 'Cookie Policy',
    cookieSettings: 'Impostazioni Cookie',
    terms: 'Termini di utilizzo',
    contact: 'Contatti',
    madeWith: 'Fatto con',
    forBusiness: 'per le aziende di tutto il mondo',
    rights: 'Tutti i diritti riservati.',
    wcagCompliance: 'Questo sito si impegna a rispettare gli standard WCAG 2.1 livello AA/AAA.',
    accessibilityContact: 'Se riscontri problemi di accessibilità, contattaci'
  },
  es: {
    brand: 'vat.expert',
    features: 'Funciones',
    legal: 'Legal',
    resources: 'Recursos',
    blog: 'Blog IVA',
    blogDesc: 'Guías, noticias y consejos',
    accessibility: 'Accesibilidad',
    popularCalculators: 'Calculadoras Populares',
    popularArticles: 'Artículos Populares',
    tools: 'Herramientas',
    allCalculators: 'Todas las Calculadoras',
    allArticles: 'Todos los Artículos',
    countries: '62 países soportados',
    languages: '5 idiomas (EN, DE, FR, IT, ES)',
    bulk: 'Procesamiento CSV masivo (hasta 1000 elementos)',
    invoice: 'Calculadora de facturas multi-tipo',
    history: 'Exportación del historial de cálculos',
    wcag: 'WCAG 2.1 AA/AAA accesible',
    keyboard: 'Navegación por teclado',
    screenReader: 'Compatible con lector de pantalla',
    contrast: 'Modo alto contraste',
    fontSize: 'Tamaño de fuente ajustable',
    motion: 'Movimiento reducido',
    dyslexia: 'Fuente para dislexia',
    privacy: 'Política de Privacidad',
    cookiePolicy: 'Política de Cookies',
    cookieSettings: 'Configuración de Cookies',
    terms: 'Términos de Servicio',
    contact: 'Contacto',
    madeWith: 'Hecho con',
    forBusiness: 'para empresas de todo el mundo',
    rights: 'Todos los derechos reservados.',
    wcagCompliance: 'Este sitio web se esfuerza por cumplir con los estándares WCAG 2.1 nivel AA/AAA.',
    accessibilityContact: 'Si experimenta problemas de accesibilidad, por favor'
  }
};

const Footer = () => {
  const { language } = useLanguage();
  const t = FOOTER_TRANSLATIONS[language] || FOOTER_TRANSLATIONS.en;
  const currentYear = new Date().getFullYear();
  
  // Get language prefix for links
  const langPrefix = language === 'en' ? '' : `/${language}`;

  // Open cookie settings
  const openCookieSettings = () => {
    localStorage.removeItem('cookie_consent');
    localStorage.removeItem('cookie_consent_timestamp');
    window.location.reload();
  };

  return (
    <footer 
      className="border-t bg-muted/30 mt-auto" 
      data-testid="footer"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div>
            <h2 className="font-bold text-xl mb-3 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              {t.brand}
            </h2>
            <p className="text-sm text-muted-foreground">
              Professional VAT calculator for businesses worldwide. Free, fast, and accessible.
            </p>
            <div className="mt-4 flex gap-2">
              <a 
                href="mailto:contact@vat.expert"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label={t.contact}
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Features */}
          <div>
            <h2 className="font-semibold mb-3 text-lg">{t.features}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground" aria-label="Application features">
              <li>• {t.countries}</li>
              <li>• {t.languages}</li>
              <li>• {t.bulk}</li>
              <li>• {t.invoice}</li>
              <li>• {t.wcag}</li>
            </ul>
          </div>

          {/* Accessibility */}
          <div>
            <h2 className="font-semibold mb-3 text-lg">{t.accessibility}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground" aria-label="Accessibility features">
              <li>• {t.keyboard}</li>
              <li>• {t.screenReader}</li>
              <li>• {t.contrast}</li>
              <li>• {t.fontSize}</li>
              <li>• {t.dyslexia}</li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h2 className="font-semibold mb-3 text-lg">{t.legal}</h2>
            <ul className="space-y-2 text-sm" aria-label="Legal links">
              <li>
                <Link 
                  to={`${langPrefix}/privacy`}
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  {t.privacy}
                </Link>
              </li>
              <li>
                <Link 
                  to={`${langPrefix}/cookies`}
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  {t.cookiePolicy}
                </Link>
              </li>
              <li>
                <Link 
                  to={`${langPrefix}/terms`}
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                >
                  <Scale className="w-4 h-4" />
                  {t.terms}
                </Link>
              </li>
              <li>
                <button
                  onClick={openCookieSettings}
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                >
                  <Cookie className="w-4 h-4" />
                  {t.cookieSettings}
                </button>
              </li>
              <li>
                <a 
                  href="mailto:contact@vat.expert"
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {t.contact}
                </a>
              </li>
            </ul>
          </div>

          {/* Resources / Blog */}
          <div>
            <h2 className="font-semibold mb-3 text-lg">{t.resources}</h2>
            <ul className="space-y-2 text-sm" aria-label="Resources">
              <li>
                <Link 
                  to={`${langPrefix}/blog`}
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 font-medium"
                >
                  <BookOpen className="w-4 h-4" />
                  {t.blog}
                </Link>
                <p className="text-xs text-muted-foreground mt-1 ml-6">{t.blogDesc}</p>
              </li>
            </ul>
          </div>
        </div>

        {/* SEO Links Section */}
        <div className="mt-8 pt-8 border-t">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Popular Calculators */}
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calculator className="w-4 h-4 text-primary" />
                {t.popularCalculators}
              </h3>
              <ul className="space-y-2" aria-label={t.popularCalculators}>
                {SEO_LINKS.popularCalculators.map(calc => (
                  <li key={calc.slug}>
                    <Link
                      to={`${langPrefix}/vat-calculator-${calc.slug}`}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {calc.name[language] || calc.name.en}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Popular Articles */}
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Newspaper className="w-4 h-4 text-primary" />
                {t.popularArticles}
              </h3>
              <ul className="space-y-2" aria-label={t.popularArticles}>
                {SEO_LINKS.popularArticles.map(article => (
                  <li key={article.slug}>
                    <Link
                      to={`${langPrefix}/blog/${article.slug}`}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {article.name[language] || article.name.en}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to={`${langPrefix}/blog`}
                    className="text-sm text-primary font-medium hover:underline"
                  >
                    {t.allArticles} &rarr;
                  </Link>
                </li>
              </ul>
            </div>

            {/* Tools */}
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-primary" />
                {t.tools}
              </h3>
              <ul className="space-y-2" aria-label={t.tools}>
                {SEO_LINKS.tools.map(tool => (
                  <li key={tool.path}>
                    <Link
                      to={`${langPrefix}${tool.path}`}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {tool.name[language] || tool.name.en}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to={`${langPrefix}/`}
                    className="text-sm text-primary font-medium hover:underline"
                  >
                    {t.allCalculators} &rarr;
                  </Link>
                </li>
              </ul>
            </div>

            {/* Quick Links / Contact */}
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" />
                {t.resources}
              </h3>
              <ul className="space-y-2" aria-label={t.resources}>
                <li>
                  <Link
                    to={`${langPrefix}/blog`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {t.blog}
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${langPrefix}/privacy`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {t.privacy}
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${langPrefix}/terms`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {t.terms}
                  </Link>
                </li>
                <li>
                  <a
                    href="mailto:contact@vat.expert"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {t.contact}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-8 pt-6 border-t">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              {t.madeWith} 
              <Heart 
                className="w-4 h-4 text-red-500" 
                fill="currentColor" 
                aria-label="love"
              /> 
              {t.forBusiness}
            </p>
            <p className="text-sm text-muted-foreground">
              © {currentYear} {t.brand}. {t.rights}
            </p>
          </div>
          
          {/* Accessibility compliance statement */}
          <div className="mt-4 text-center">
            <p className="text-xs text-muted-foreground">
              {t.wcagCompliance}{' '}
              {t.accessibilityContact}{' '}
              <a 
                href="mailto:accessibility@vat.expert" 
                className="text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
              >
                contact us
              </a>.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
